import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import CheckboxWidget from '@plone/volto/components/manage/Widgets/CheckboxWidget';
import SelectWidget from '@plone/volto/components/manage/Widgets/SelectWidget';
import NumberWidget from '@plone/volto/components/manage/Widgets/NumberWidget';

const messages = defineMessages({
  audioOnlyModeTitle: {
    id: 'audioOnlyModeTitle',
    defaultMessage: 'Audio Only Mode',
  },
  audioOnlyModeDescription: {
    id: 'audioOnlyModeDescription',
    defaultMessage:
      'If set to true, hides all player components that are video specific.',
  },
  autoplayTitle: {
    id: 'autoplayTitle',
    defaultMessage: 'Autoplay',
  },
  autoplayDescription: {
    id: 'autoplayDescription',
    defaultMessage:
      'NOTE: At this point, the autoplay attribute and option are NOT a guarantee that your video will autoplay.',
  },
  controlsTitle: {
    id: 'controlsTitle',
    defaultMessage: 'Controls',
  },
  controlsDescription: {
    id: 'controlsDescription',
    defaultMessage:
      'Determines whether or not the player has controls that the user can interact with. Without controls the only way to start the video playing is with the autoplay attribute.',
  },
  widthTitle: {
    id: 'widthTitle',
    defaultMessage: 'Width',
  },
  widthDescription: {
    id: 'widthDescription',
    defaultMessage: 'Sets the display width of the video player in pixels.',
  },
  heightTitle: {
    id: 'heightTitle',
    defaultMessage: 'Height',
  },
  heightDescription: {
    id: 'heightDescription',
    defaultMessage: 'Sets the display height of the video player in pixels.',
  },
  loopTitle: {
    id: 'loopTitle',
    defaultMessage: 'Loop',
  },
  loopDescription: {
    id: 'loopDescription',
    defaultMessage: 'Causes the video to start over as soon as it ends.',
  },
  responsiveTitle: {
    id: 'responsiveTitle',
    defaultMessage: 'Responsive Controls',
  },
  responsiveDescription: {
    id: 'responsiveDescription',
    defaultMessage:
      'Setting this option to true will cause the player to customize itself based on responsive breakpoints (see: breakpoints option). When this option is false (the default), responsive breakpoints will be ignored. Note this is about the responsiveness of the controls within the player, not responsive sizing of the player itself. For that, see fluid.',
  },

  liveuiTitle: {
    id: 'liveuiTitle',
    defaultMessage: 'Live UI',
  },
  liveuiDescription: {
    id: 'liveuiDescription',
    defaultMessage:
      'Allows the player to use the new live ui that includes: 1. A progress bar for seeking within the live window 2. A button that can be clicked to seek to the live edge with a circle indicating if you are at the live edge or not. Without this option the progress bar will be hidden and in its place will be text that indicates LIVE playback. There will be no progress control and you will not be able to click the text to seek to the live edge. liveui will default to true in a future version!',
  },
  mutedTitle: {
    id: 'mutedTitle',
    defaultMessage: 'Muted',
  },
  mutedDescription: {
    id: 'mutedDescription',
    defaultMessage: 'Will silence any audio by default.',
  },
  playbackRatesTitle: {
    id: 'playbackRatesTitle',
    defaultMessage: 'Playback Rates',
  },
  playbackRatesDescription: {
    id: 'playbackRatesDescription',
    defaultMessage:
      'An array of numbers strictly greater than 0, where 1 means regular speed (100%), 0.5 means half-speed (50%), 2 means double-speed (200%), etc. If specified, Video.js displays a control allowing the user to choose playback speed from among the array of choices. The choices are presented in the specified order from bottom to top.',
  },
});

const Options = (props) => {
  const { block, data, onChangeBlock, refreshPlayer } = props;
  const intl = useIntl();

  return (
    <>
      <CheckboxWidget
        id="audioOnlyMode"
        value={data.options?.audioOnlyMode}
        onChange={(id, value) => {
          onChangeBlock(block, {
            ...data,
            options: {
              ...data.options,
              [id]: value,
            },
          });
          refreshPlayer();
        }}
        key="audioOnlyMode"
        description={intl.formatMessage(messages.audioOnlyModeDescription)}
        block={block}
        title={intl.formatMessage(messages.audioOnlyModeTitle)}
      />
      <CheckboxWidget
        id="autoplay"
        value={data.options?.autoplay}
        onChange={(id, value) => {
          onChangeBlock(block, {
            ...data,
            options: {
              ...data.options,
              [id]: value,
            },
          });
          refreshPlayer();
        }}
        key="autoplay"
        description={intl.formatMessage(messages.autoplayDescription)}
        block={block}
        title={intl.formatMessage(messages.autoplayTitle)}
      />
      <CheckboxWidget
        id="controls"
        value={data.options?.controls}
        onChange={(id, value) => {
          onChangeBlock(block, {
            ...data,
            options: {
              ...data.options,
              [id]: value,
            },
          });
          refreshPlayer();
        }}
        key="controls"
        description={intl.formatMessage(messages.controlsDescription)}
        block={block}
        title={intl.formatMessage(messages.controlsTitle)}
      />
      <NumberWidget
        id="width"
        value={data.options?.width}
        onChange={(id, value) => {
          onChangeBlock(block, {
            ...data,
            options: {
              ...data.options,
              [id]: value,
            },
          });
          refreshPlayer();
        }}
        key="width"
        description={intl.formatMessage(messages.widthDescription)}
        block={block}
        title={intl.formatMessage(messages.widthTitle)}
      />
      <NumberWidget
        id="height"
        value={data.options?.height}
        onChange={(id, value) => {
          onChangeBlock(block, {
            ...data,
            options: {
              ...data.options,
              [id]: value,
            },
          });
          refreshPlayer();
        }}
        key="height"
        description={intl.formatMessage(messages.heightDescription)}
        block={block}
        title={intl.formatMessage(messages.heightTitle)}
      />
      <CheckboxWidget
        id="loop"
        value={data.options?.loop}
        onChange={(id, value) => {
          onChangeBlock(block, {
            ...data,
            options: {
              ...data.options,
              [id]: value,
            },
          });
          refreshPlayer();
        }}
        key="loop"
        description={intl.formatMessage(messages.loopDescription)}
        block={block}
        title={intl.formatMessage(messages.loopTitle)}
      />
      <CheckboxWidget
        id="responsive"
        value={data.options?.responsive}
        onChange={(id, value) => {
          onChangeBlock(block, {
            ...data,
            options: {
              ...data.options,
              [id]: value,
            },
          });
          refreshPlayer();
        }}
        key="responsive"
        description={intl.formatMessage(messages.responsiveDescription)}
        block={block}
        title={intl.formatMessage(messages.responsiveTitle)}
      />

      <CheckboxWidget
        id="liveui"
        value={data.options?.liveui}
        onChange={(id, value) => {
          onChangeBlock(block, {
            ...data,
            options: {
              ...data.options,
              [id]: value,
            },
          });
          refreshPlayer();
        }}
        key="liveui"
        description="Allows the player to use the new live ui that includes:
                        1. A progress bar for seeking within the live window
                        2. A button that can be clicked to seek to the live edge with a circle indicating if you are at the live edge or not.
                        Without this option the progress bar will be hidden and in its place will be text that indicates LIVE playback. There will be no progress control and you will not be able to click the text to seek to the live edge. liveui will default to true in a future version!"
        block={block}
        title={intl.formatMessage(messages.liveuiTitle)}
      />
      <CheckboxWidget
        id="muted"
        value={data.options?.muted}
        onChange={(id, value) => {
          onChangeBlock(block, {
            ...data,
            options: {
              ...data.options,
              [id]: value,
            },
          });
          refreshPlayer();
        }}
        key="muted"
        description={intl.formatMessage(messages.mutedDescription)}
        block={block}
        title={intl.formatMessage(messages.mutedTitle)}
      />
      <SelectWidget
        id="playbackRates"
        value={data.options?.playbackRates}
        onChange={(id, value) => {
          if (value.length) {
            onChangeBlock(block, {
              ...data,
              options: {
                ...data.options,
                [id]: value,
              },
            });
          } else {
            const { [id]: _removed, ...restOptions } = data.options || {};
            onChangeBlock(block, {
              ...data,
              options: restOptions,
            });
          }
          refreshPlayer();
        }}
        choices={[
          [0.5, '0.5'],
          [0.75, '0.75'],
          [1, '1'],
          [1.25, '1.25'],
          [1.5, '1.5'],
          [2, '2'],
        ]}
        noValueOption={false}
        isMulti={true}
        key="playbackRates"
        description={intl.formatMessage(messages.playbackRatesDescription)}
        block={block}
        title={intl.formatMessage(messages.playbackRatesTitle)}
      />
    </>
  );
};

export default Options;
